import { useQueryClient } from '@tanstack/react-query';
import { effectivePermissionSetHas } from '@utazzo/shared';
import { cn, UzButton, UzTabs } from '@utazzo/ui';
import { Plus, RefreshCw } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { LeadsBusinessStrip } from '../components/LeadsBusinessStrip';
import { OwnerFilter } from '../components/OwnerFilter';
import { isoDate } from '../lead-date-range';
import { useAgentOptionsForLeadsWithRole } from '../use-lead-options';

import { LeadsListPage } from './LeadsListPage';

import { PageShell } from '@/components/page-shell';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useMyEffectivePermissions } from '@/features/my-profile/use-my-profile';
import { MonthYearFilter } from '@/features/reports/components/MonthYearFilter';
import { DailyTrackerPage } from '@/features/reports/pages/DailyTrackerPage';
import { useUrlParamWriter } from '@/hooks/use-url-param-writer';

type Tab = 'pipeline' | 'long-followup' | 'all';
const isTab = (v: string | null): v is Tab =>
  v === 'pipeline' || v === 'long-followup' || v === 'all';

/**
 * Lead inbox - the home of the leads admin module.
 *
 * Wraps two tabs (per `u-desgin/feature-leads.jsx` `FeatureLeads`):
 *   - **Pipeline** - KPI strip + per-stage tiles + today's leads list.
 *   - **All leads** - full DataGrid with filters, re-uses `LeadsListPage` in
 *     embedded mode so the page header is owned here.
 *
 * Tab state is URL-driven (`?tab=pipeline|all`) so operators can share a
 * direct link. The Import button is rendered (matching the design) but
 * disabled until the Phase A2 bulk-Excel endpoint ships (see ADR-0032 §8).
 */
export const LeadInboxPage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // Every write below merges into the LIVE URL: the All-leads tab runs a
  // nuqs-backed DataGrid whose keys (`page`, `pageSize`, `sortBy`, `f.*`)
  // React Router's `params` snapshot never sees.
  const writeParams = useUrlParamWriter();
  const me = useCurrentUser();
  const queryClient = useQueryClient();
  // Top-level "Refresh everything" (2026-10-01): one button that re-pulls the
  // whole inbox - the live Today strips, the Targets strip, and the embedded
  // Daily Tracker report - by invalidating every active query. Broad on
  // purpose so it also covers tiles/rows added later without re-wiring.
  const [refreshing, setRefreshing] = useState(false);
  const refreshAll = async (): Promise<void> => {
    setRefreshing(true);
    try {
      await queryClient.invalidateQueries();
    } finally {
      setRefreshing(false);
    }
  };
  // The Lead Inbox has no date-range picker (historical drill-down lives in
  // Reports); it is a current-work snapshot. The Daily Tracker strip now lives
  // in the embedded report below, which owns its own period.
  // "All leads" tab: admin, sales lead and (since 2026-10-01) sales agent,
  // each browsing their own scoped inventory - the server scopes `/leads`
  // per role, so an agent sees exactly the leads assigned to them, with the
  // same KPI strip and grid a team lead gets for their team. OPS / FINANCE /
  // READ_ONLY work from Pipeline.
  const canSeeAllLeadsTab =
    me?.role === 'ADMIN' || me?.role === 'SALES_LEAD' || me?.role === 'SALES_AGENT';
  // The Total Pool tab hosts the full Daily Tracker report below Targets, but
  // reports are org-wide - gate on `ops.lead_reports.read` (ADMIN bypasses).
  // Users without it see no report there (Targets/tracker still show).
  const myPerms = useMyEffectivePermissions();
  const canSeeReports =
    me?.role === 'ADMIN' ||
    (myPerms instanceof Set && effectivePermissionSetHas(myPerms, 'ops.lead_reports.read'));
  const raw = params.get('tab');
  const requested: Tab = isTab(raw) ? raw : 'pipeline';
  // Non-admins can't land on `?tab=all` even via a direct/shared link.
  const tab: Tab = requested === 'all' && !canSeeAllLeadsTab ? 'pipeline' : requested;

  // 2026-08-01. Date params are NO LONGER stored in the URL - the inbox is a
  // hard-coded snapshot per tab (this-month + carry-forward for Pipeline/All;
  // all-time for Long Follow-up, passed as a `rangePreset` prop to that tab's
  // grid). So this just STRIPS any stale/incoming date params, keeping the URL
  // clean (tab + ownerId + drill flags only). `rangeLocked` + `lockedBounds`
  // below still supply the effective range to the widgets from code.
  const stripDateParams = (into: URLSearchParams): void => {
    into.delete('daterange');
    into.delete('from');
    into.delete('to');
    // Carry-forward is strict-on in code now, so the flag is never stored.
    into.delete('f.activeCarryover');
    into.delete('activeCarryover');
  };

  const setTab = (next: Tab): void => {
    const nextParams = new URLSearchParams(params);
    nextParams.set('tab', next);
    // Strip any stale date params so the URL stays clean across tab switches.
    stripDateParams(nextParams);
    setParams(nextParams, { replace: true });
  };

  // Scrub stale `?tab=all` from the URL for roles that can't see the
  // tab, so the address bar matches the visible tab. Runs once per
  // role change.
  //
  // 2026-07-10 fix. Previously only exempted ADMIN from the scrub,
  // but `canSeeAllLeadsTab` also allows SALES_LEAD. That role
  // clicking "All leads" would flash the tab, then this effect
  // would scrub the URL back to `?tab=pipeline` and force a second
  // re-render onto Total Pool - the visible "glitch on clicking All
  // leads". Gate on the same predicate the tab-list uses.
  useEffect(() => {
    if (requested !== 'all') return;
    if (canSeeAllLeadsTab) return;
    const nextParams = new URLSearchParams(params);
    nextParams.set('tab', 'pipeline');
    stripDateParams(nextParams);
    setParams(nextParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested, canSeeAllLeadsTab]);

  // URL-cleanliness lock: date params are never stored (hard-coded in code),
  // so strip any that arrive via typed URLs, shared/bookmarked links, or a
  // drill-in that still forwards `daterange=this_month`. Keeps the address bar
  // to tab + ownerId + drill flags. Runs for everyone (canChangeDateRange is
  // always false now).
  useEffect(() => {
    const nextParams = new URLSearchParams(params);
    const before = nextParams.toString();
    stripDateParams(nextParams);
    if (nextParams.toString() !== before) {
      setParams(nextParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // 2026-07-17 fix. A URL can carry `daterange=<preset>` WITHOUT from/to
  // (shared links, drill-ins that only forward the preset key). Previously
  // the raw `params.get('from'/'to')` (null) flowed into every child strip,
  // so all tracker/KPI queries ran UNBOUNDED - "Total Leads 63k" while the
  // header said "This month", self-healing only after the operator re-picked
  // a preset. Named presets are relative windows resolved at read time
  // (lead-date-range.ts contract), so fall back to the preset's computed
  // bounds whenever the URL ones are absent. Explicit URL bounds still win
  // (they pin the window so refresh-tomorrow doesn't slide it).
  // 2026-07-08. Role-scoped owner filter - drives every child (KPI strips,
  // pipeline tiles, trackers, All-leads grid) through the same URL param
  // so shared links honour the filter and every widget stays in sync.
  const ownerId = params.get('ownerId');
  const setOwnerId = (next: string | null): void => {
    writeParams((p) => {
      if (next) p.set('ownerId', next);
      else p.delete('ownerId');
    });
  };
  // 2026-07-13. Resolve the picked owner's role so the Business
  // (Target) strip can pick the right rendering mode (SALES_AGENT
  // owner => single tile; SALES_LEAD owner => split my + team). The
  // same hook already backs the OwnerFilter's dropdown - React Query
  // dedupes so this is a cache hit, not an extra request.
  const { data: agentOptions } = useAgentOptionsForLeadsWithRole();
  const pickedOwner = ownerId ? (agentOptions.find((a) => a.id === ownerId) ?? null) : null;
  const ownerRole: 'SALES_AGENT' | 'SALES_LEAD' | null =
    pickedOwner?.role === 'SALES_LEAD' || pickedOwner?.role === 'SALES_AGENT'
      ? pickedOwner.role
      : null;
  const ownerName = pickedOwner?.fullName ?? null;

  // Reporting period = a single MONTH + YEAR (default: current month), stored
  // in `?month&year` and read by the embedded Daily Tracker (Section 3). It is
  // INDEPENDENT of the removed date-range picker (`stripDateParams` never
  // touches month/year), so the live "Today" strips stay this-month while the
  // report below follows whatever month is picked here.
  const nowRef = useMemo(() => new Date(), []);
  const selMonth = Number(params.get('month')) || nowRef.getMonth() + 1; // 1-12
  const selYear = Number(params.get('year')) || nowRef.getFullYear();
  const setMonthYear = (month: number, year: number): void => {
    writeParams((p) => {
      p.set('month', String(month));
      p.set('year', String(year));
    });
  };
  // Targets follow the selected month too (user: "last month, what was the
  // target and what is achieved"). A past month pins the quota period
  // (`periodStart`) AND the Files-Converted window to that whole month; the
  // current month passes nothing, so its behaviour is byte-identical to before.
  const isSelCurrentMonth = selYear === nowRef.getFullYear() && selMonth === nowRef.getMonth() + 1;
  const targetMonth = isSelCurrentMonth
    ? null
    : {
        periodStart: isoDate(new Date(selYear, selMonth - 1, 1))!,
        wonFrom: isoDate(new Date(selYear, selMonth - 1, 1))!,
        wonTo: isoDate(new Date(selYear, selMonth, 0))!,
      };

  // Section mini-label meta (Claude Design v1): "October 2026" period + today's
  // date, shown on the Targets / Daily Tracker strip headers.
  const periodLabel = new Date(selYear, selMonth - 1, 1).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <PageShell
      title="Lead inbox"
      description="Enquiries from every channel. Track them from first touch to confirmed booking."
      actions={
        // Phones: the header's actions slot never shrinks, so pin this row to
        // the <main> content width (viewport minus its px-4) and let it wrap.
        <div className="flex flex-wrap items-center gap-2 max-sm:w-[calc(100vw-2rem)]">
          {/* Reporting month/year (2026-10-01). Drives the embedded Daily
              Tracker report (Section 3) only - the live "Today" strips above it
              stay this-month. Shown on the Total Pool tab for users who can see
              the report; it controls nothing on the other tabs yet. */}
          {tab === 'pipeline' && canSeeReports && (
            <MonthYearFilter month={selMonth} year={selYear} onChange={setMonthYear} />
          )}
          {/* Owner filter (2026-07-08) - role-scoped picker at the far
              right. ADMIN sees every SALES_LEAD + SALES_AGENT, a
              SALES_LEAD sees themselves + direct reports, a SALES_AGENT
              is hidden entirely (they only see their own leads anyway).
              Drives every child via the shared `?ownerId=` param. */}
          <OwnerFilter value={ownerId} onValueChange={setOwnerId} className="w-44" />
          {/* Date-range picker removed 2026-08-01 - the inbox is a fixed
              this-month + carry-forward snapshot for current work. Historical
              date filtering lives in Reports. `canChangeDateRange` is now always
              false, which locks every widget to the this-month tab default. */}
          {/* Import removed 2026-10-01 - lead ingestion now runs through the
              Lead Parsing System, so the inbox no longer needs a bulk-XLSX
              button. */}
          <UzButton
            variant="outline"
            size="sm"
            onClick={() => void refreshAll()}
            disabled={refreshing}
            title="Refresh all tiles and the report"
          >
            <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
          </UzButton>
          <UzButton size="sm" onClick={() => navigate('/leads/new')}>
            <Plus className="mr-1.5 h-4 w-4" />
            New lead
          </UzButton>
        </div>
      }
    >
      <UzTabs
        value={tab}
        onValueChange={(v) => setTab(v as Tab)}
        tabs={[
          {
            value: 'pipeline',
            label: 'Total Pool',
            content: (
              <>
                {/* Daily Leads Tracker (left, 6 actionable tiles) + Business
                    Numbers (right, 4 read-only tiles) sit side-by-side on
                    lg+, mirroring the legacy Daily Leads Tracker layout
                    (operational on the left, financial highlight on the
                    right). They stack vertically on narrower viewports.
                    Split is 6/4 of a 10-col grid - target tiles need
                    more horizontal room to show big quotas without
                    truncating ("₹1.25Cr" etc.), the tracker's 6-tile
                    grid still reads cleanly inside 6 columns because
                    each row of 4 wraps to 2 rows of 3 anyway. See
                    ADR-0032 §21 / §24. */}
                <div className="mb-3 flex flex-col gap-3">
                  {/* Targets first (2026-10-01) - the quota headline sits above
                      the operational tracker. Follows the Month/Year filter:
                      a past month shows that month's target vs achieved. */}
                  <div>
                    <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2">
                      <div className="text-[11px] font-semibold tracking-[0.06em] text-zinc-500 uppercase">
                        Targets <span className="font-medium text-zinc-400">· {periodLabel}</span>
                      </div>
                      <div className="text-[11px] text-zinc-500">Team · turnover basis</div>
                    </div>
                    <LeadsBusinessStrip
                      ownerId={ownerId}
                      ownerRole={ownerRole}
                      ownerName={ownerName}
                      periodStart={targetMonth?.periodStart ?? null}
                      wonFrom={targetMonth?.wonFrom ?? null}
                      wonTo={targetMonth?.wonTo ?? null}
                    />
                  </div>
                  {/* The Daily Tracker strip now lives inside the embedded report
                      below (positioned under Today's Pipeline), so it is no longer
                      rendered here - that removes the earlier duplicate. */}
                </div>
                {/* Full Daily Tracker report below Targets, driven by the
                    inbox's own DateRangeFilter (same URL params the report
                    reads). Replaces the old pipeline list. Gated: only shown
                    to users holding ops.lead_reports.read (ADMIN bypasses). */}
                {canSeeReports && <DailyTrackerPage embedded />}
              </>
            ),
          },
          // Long Follow-up: every role sees this tab - it's just a
          // pre-filtered slice of the leads they already have
          // access to, identical visibility rules to the Pipeline
          // tab. Server-side RBAC on `/leads` still scopes the
          // underlying response (an agent sees their own
          // long-followups, a sales lead sees their team's, etc.).
          // The `forcedStatusCode` lock on LeadsListPage stops the
          // operator from widening the filter from inside the tab.
          {
            value: 'long-followup' as const,
            label: 'Long Follow-up',
            content: (
              <LeadsListPage
                embedded
                forcedStatusCode="long_followup"
                rangePreset="all"
                ownerId={ownerId}
              />
            ),
          },
          // Browse view, scoped by the server-side RBAC on /leads: every
          // lead for ADMIN, the team's for SALES_LEAD, their own for
          // SALES_AGENT. Other roles never see this tab.
          ...(canSeeAllLeadsTab
            ? [
                {
                  value: 'all',
                  label: 'All leads',
                  content: (
                    // KPI tiles removed from All leads (2026-10-02) - the tab is
                    // a plain browse grid now; the pool/bucket metrics live on
                    // Total Pool. No status exclusion: "All leads" means all,
                    // including long_followup.
                    <LeadsListPage embedded enableActiveCarryover ownerId={ownerId} />
                  ),
                },
              ]
            : []),
        ]}
      />
    </PageShell>
  );
};
